// src/pages/ConviteFazenda/conviteFazendaLogica.js
// Lógica pura do convite nominal de responsável por fazenda (PG-042).
//
// Tudo que decide comportamento mora AQUI, fora do JSX: predicado de
// visibilidade da ação, higiene de entrada, montagem do body, extração da
// mensagem do backend, leitura do token da query, destino pós-login e a
// orquestração das duas chamadas de API do fluxo (gerar convite / aceitar
// convite). O motivo é concreto: não há um único teste de renderização em
// src/ (@testing-library/react 9.5.0 x React 19 é incompatível), então o que
// ficasse preso dentro do render seria inobservável.
//
// REGRA DURA DESTE MÓDULO: nenhuma mensagem de erro do backend aparece aqui
// como literal de string — nem em comparação, nem em mapa, nem em switch.
// O backend hoje tem 10 mensagens, uma delas interpolada (muda sozinha se o
// máximo de horas mudar lá). O front faz uma coisa só com a mensagem: exibir
// o que veio.

// `redux/api/actions.js` NÃO é importado no topo do módulo de propósito: ele
// arrasta `redux/auth/actions.js` → `database/index.js` →
// `database/resources/utils.js`, que por sua vez exige `react-router-dom` —
// e é exatamente essa cadeia que já derruba `src/sync/__tests__/
// CircuitBreaker.test.js` na linha de base deste repo (falha pré-existente,
// não deste card). Um import estático aqui quebraria TODO teste que requer
// este módulo só para usar um predicado puro (ex.: `podeConvidarResponsavel`)
// e não mocka `redux/api/actions` — que é a maioria dos arquivos de teste
// deste card. `require` tardio, só dentro de `enviarConvite`/`aceitarConvite`
// (as duas únicas funções que de fato chamam a API), adia a resolução do
// módulo até o momento em que ele é usado — mesmo raciocínio do dynamic
// import em `src/observability/events.ts` (`triggerImmediatePush`).
function apiActions() {
  // eslint-disable-next-line global-require
  return require('../../redux/api/actions.js')
}

/** Path da rota de aceite. Fixado pelo formato do link que o backend emite. */
export const ROTA_CONVITE = '/convite-fazenda'

/** Nome do parâmetro de query que carrega o token do convite. */
export const PARAM_TOKEN = 't'

/** Rota do serviço `users` (porta 81) que gera o convite. */
export const ROTA_API_GERAR = 'ConviteFazenda'

/** Rota do serviço `users` que aceita o convite. */
export const ROTA_API_ACEITAR = 'ConviteFazenda/aceitar'

// Textos PRÓPRIOS do front. Existem porque nestes caminhos nenhuma resposta do
// backend existe para ser exibida: a requisição não chega nele.
export const MENSAGEM_EMAIL_INVALIDO =
  'Informe um e-mail válido para enviar o convite.'
export const MENSAGEM_PRAZO_INVALIDO =
  'O prazo deve ser um número inteiro de horas.'
export const MENSAGEM_LINK_INCOMPLETO =
  'Este link de convite está incompleto. Peça a quem convidou para enviar o link completo.'
// Rede caiu / resposta sem envelope: o backend não chegou a responder, então
// não existe mensagem dele a exibir — e este caminho É alcançável num PWA de
// campo.
export const MENSAGEM_SEM_RESPOSTA =
  'Não foi possível falar com o servidor agora. Verifique a conexão e abra o link novamente.'

/** Estados possíveis da tela de aceite. Mutuamente exclusivos. */
export const ESTADO_ACEITE = {
  LINK_INCOMPLETO: 'link-incompleto',
  CARREGANDO: 'carregando',
  SEM_RESPOSTA: 'sem-resposta',
  ERRO: 'erro',
  SUCESSO: 'sucesso',
}

// Higiene de FORMA, não de regra de negócio. É o mesmo que o ModelState do
// backend checa ([EmailAddress]) — e é justamente o caminho em que o backend
// devolve string crua no lugar do envelope {sucesso, mensagem}, fazendo o
// hasErrors de redux/api/actions.js sair false e uma recusa virar API_SUCCESS.
// Não enviar a requisição malformada é a mitigação que cabe no front.
const FORMATO_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Higiene de TIPO: o backend recebe `int?`. Dígitos apenas — o que recusa
// '3,5', '3.5', 'abc' e '-2' pela forma. A FAIXA (1..72) é regra de negócio do
// backend e NÃO é replicada aqui: '100' passa por esta higiene e é enviado.
const FORMATO_PRAZO_INTEIRO = /^\d+$/

/**
 * A ação "Convidar responsável" só existe para o criador da fazenda.
 * `farm.usuarioId` vem do DTO de Farms (referências mapeiam user_id).
 * `usuarioLogado` é o objeto INTEIRO devolvido por getLoggedUser() (não só o
 * id) — é essa a forma que o contrato fixa, para casar com o formato real do
 * chamador sem exigir que ele desembrulhe `.userId` antes de perguntar.
 */
export function podeConvidarResponsavel(farm, usuarioLogado) {
  const userIdLogado = usuarioLogado && usuarioLogado.userId
  if (!farm || !farm.usuarioId || !userIdLogado) return false
  return farm.usuarioId === userIdLogado
}

/**
 * Normaliza o campo de prazo: devolve a string do que o usuário informou, ou
 * `null` quando ele deixou o campo vazio. Vazio é ausência, não zero.
 */
function prazoInformado(prazo) {
  if (prazo === null || prazo === undefined) return null
  const texto = String(prazo).trim()
  return texto === '' ? null : texto
}

/**
 * Higiene de entrada do modal de geração.
 * Devolve { valido, erros: { emailConvidado, prazo } } — `erros` traz texto
 * próprio do front, nunca mensagem do backend.
 */
function validarEntradaConvite({ emailConvidado, prazo } = {}) {
  const erros = {}

  const emailNormalizado =
    typeof emailConvidado === 'string' ? emailConvidado.trim() : ''
  if (!FORMATO_EMAIL.test(emailNormalizado)) {
    erros.emailConvidado = MENSAGEM_EMAIL_INVALIDO
  }

  const prazoNormalizado = prazoInformado(prazo)
  if (
    prazoNormalizado !== null &&
    !FORMATO_PRAZO_INTEIRO.test(prazoNormalizado)
  ) {
    erros.prazo = MENSAGEM_PRAZO_INVALIDO
  }

  return { valido: Object.keys(erros).length === 0, erros }
}

/**
 * Monta o body do POST de geração.
 * `expiraEmHoras` só existe como chave quando o usuário informou um prazo —
 * não vai como null, nem undefined, nem com um default de 24 inventado pelo
 * front (omitir é o que faz o backend aplicar o default dele).
 */
function montarCorpoConvite({ fazendaId, emailConvidado, prazo } = {}) {
  const corpo = {
    fazendaId,
    emailConvidado:
      typeof emailConvidado === 'string'
        ? emailConvidado.trim()
        : emailConvidado,
  }

  const prazoNormalizado = prazoInformado(prazo)
  if (prazoNormalizado !== null) {
    corpo.expiraEmHoras = Number(prazoNormalizado)
  }

  return corpo
}

/**
 * Higiene de entrada + montagem do body num só passo, fora do JSX.
 * Devolve `{ valido: true, body }` quando a entrada passa na higiene de
 * FORMA, ou `{ valido: false, erros: { emailConvidado?, prazo? } }` quando
 * não passa — sem `body` neste caso, para que ninguém envie por engano.
 */
export function prepararConvite({ fazendaId, emailConvidado, prazo } = {}) {
  const { valido, erros } = validarEntradaConvite({ emailConvidado, prazo })
  if (!valido) return { valido: false, erros }

  return {
    valido: true,
    body: montarCorpoConvite({ fazendaId, emailConvidado, prazo }),
  }
}

/**
 * Envia o convite: aplica a higiene e só dispara o POST quando ela passa.
 * Quando a entrada é inválida, devolve o próprio resultado de `prepararConvite`
 * (contendo `erros`) sem fazer nenhuma requisição — é o caller quem decide o
 * que fazer com isso (ex.: exibir os erros de campo).
 */
export async function enviarConvite(dispatch, entrada) {
  const preparado = prepararConvite(entrada)
  if (!preparado.valido) return preparado

  return apiActions().post(dispatch, 'users', ROTA_API_GERAR, preparado.body)
}

/**
 * Só existe controle de copiar quando o resultado trouxe um link não-vazio.
 * Sem isso, o defeito de contrato do _modeloInvalido (recusa despachada como
 * API_SUCCESS, sem link) renderizaria um botão que copia string vazia.
 */
export function podeCopiarLink(resultado) {
  const link = resultado && resultado.link
  return typeof link === 'string' && link.trim() !== ''
}

/** Lê o link da resposta de geração, EXATAMENTE como veio — sem trim, sem encode. */
export function linkParaCopiar(resultado) {
  if (!podeCopiarLink(resultado)) return null
  return resultado.link
}

/**
 * Extrai a mensagem do backend do resultado de redux/api/actions.js.
 * A mensagem chega em `result.title` (actions.js: `title: result.title ||
 * result.mensagem`) — quem procurar `result.mensagem` renderiza undefined.
 * Devolve o texto LITERAL, sem prefixo, sem tradução, sem reinterpretação.
 */
export function extrairMensagemDeErro(resultado) {
  const mensagem = resultado && resultado.title
  return typeof mensagem === 'string' && mensagem !== '' ? mensagem : null
}

/**
 * Houve recusa do backend? Usa o hasErrors que o próprio helper de API monta.
 * Exportada porque os dois componentes-casca (geração e aceite) precisam
 * decidir entre mostrar a mensagem de erro e seguir o caminho de sucesso —
 * fora dessas 13 funções listadas no contrato, mas é leitura de forma, não
 * regra de negócio.
 */
export function houveErroDeBackend(resultado) {
  return Boolean(resultado && resultado.hasErrors)
}

/**
 * Token do convite a partir da query string da rota de aceite.
 * Devolve `null` quando `?t=` está ausente ou vazio — e é esse null que
 * impede a requisição de aceite de ser disparada.
 */
export function extrairTokenDaBusca(search) {
  if (typeof search !== 'string' || search === '') return null
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  )
  const token = params.get(PARAM_TOKEN)
  return typeof token === 'string' && token.trim() !== '' ? token : null
}

/** Monta a rota da fazenda a partir do id devolvido pelo aceite. */
export function rotaDaFazenda(fazendaId) {
  return `/farms/${fazendaId}`
}

/**
 * O `from` que o PrivateRoute injeta só é honrado quando aponta para a rota de
 * convite. Qualquer outra origem — inclusive `from` ausente — continua caindo
 * na home, exatamente como hoje. Este é o contrapeso da decisão D1: o raio de
 * alcance da mudança no login precisa ser verificável.
 */
function deveHonrarFrom(from) {
  return Boolean(from) && from.pathname === ROTA_CONVITE
}

/**
 * Destino pós-login. Recebe o `location` do LoginGigya (via withRouter).
 * Preserva a query string da origem — é ela que carrega o `?t=` do convite.
 */
export function destinoAposLogin(location) {
  const from = location && location.state && location.state.from
  if (!deveHonrarFrom(from)) return '/'
  return `${from.pathname}${from.search || ''}`
}

/** Monta o body do POST de aceite — a única chave é `token`. */
export function montarCorpoDeAceite(token) {
  return { token }
}

/**
 * Existe token para consultar o backend? Sem token não há requisição de
 * aceite a fazer: a tela mostra texto próprio do front (link incompleto), e
 * o backend nunca é consultado.
 */
export function deveAceitar(token) {
  return Boolean(token)
}

/** Aceita o convite: POST para `users` / `ConviteFazenda/aceitar`. */
export async function aceitarConvite(dispatch, token) {
  return apiActions().post(
    dispatch,
    'users',
    ROTA_API_ACEITAR,
    montarCorpoDeAceite(token),
  )
}

/**
 * Um aceite bem-sucedido, com fazendaId devolvido, deve disparar o pull do
 * sync — é ele que traz o vínculo novo (G018: o loop de background é
 * push-only por default; o pull aqui precisa ser explícito). Erro do backend
 * ou resposta sem fazendaId não disparam pull nenhum.
 */
export function deveDispararPull(resultado) {
  if (!resultado || houveErroDeBackend(resultado)) return false
  return Boolean(resultado.fazendaId)
}

/**
 * Estado da tela de aceite, escolhido fora do JSX e mutuamente exclusivo.
 *
 * - `token` ausente/vazio → 'link-incompleto': a requisição nunca foi feita,
 *   e nenhuma `resposta` pendurada no estado pode vazar para a tela.
 * - `resposta` ainda `undefined` → 'carregando': a requisição está em voo.
 * - `resposta === null` → 'sem-resposta': `api.post` devolveu null (offline,
 *   rede caída) — não é recusa do convite, não existe mensagem do backend.
 * - `resposta.hasErrors` → 'erro': mensagem literal do backend; nome/link de
 *   fazenda nunca aparecem aqui, mesmo que `fazendaLocal` exista por acaso.
 * - do contrário → 'sucesso': o convite FOI aceito no servidor. Isso vale
 *   mesmo que o pull tenha falhado ou que a fazenda ainda não esteja no banco
 *   local (`fazendaLocal` ausente) — nome e link degradam graciosamente, mas
 *   o estado nunca vira erro por causa de sync. `pullFalhou` é aceito na
 *   entrada só para documentar essa garantia no chamador; não influencia o
 *   `tipo` devolvido.
 */
export function estadoDaTelaDeAceite({ token, resposta, fazendaLocal } = {}) {
  if (!deveAceitar(token)) return { tipo: ESTADO_ACEITE.LINK_INCOMPLETO }
  if (resposta === undefined) return { tipo: ESTADO_ACEITE.CARREGANDO }
  if (resposta === null) return { tipo: ESTADO_ACEITE.SEM_RESPOSTA }

  if (houveErroDeBackend(resposta)) {
    return {
      tipo: ESTADO_ACEITE.ERRO,
      mensagem: extrairMensagemDeErro(resposta),
    }
  }

  const fazendaId = resposta.fazendaId || null
  const nomeDaFazenda =
    (fazendaLocal && fazendaLocal.id === fazendaId && fazendaLocal.nome) ||
    undefined

  return {
    tipo: ESTADO_ACEITE.SUCESSO,
    fazendaId,
    nomeDaFazenda,
    href: fazendaId ? rotaDaFazenda(fazendaId) : undefined,
  }
}

/**
 * Copia exatamente o texto recebido para a área de transferência.
 * Fallback por textarea porque o app roda em campo, e `navigator.clipboard`
 * não existe fora de contexto seguro.
 */
export async function copiarParaAreaDeTransferencia(texto) {
  if (typeof texto !== 'string' || texto.trim() === '') return false

  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      navigator.clipboard.writeText
    ) {
      await navigator.clipboard.writeText(texto)
      return true
    }
  } catch (e) {
    // cai no fallback abaixo
  }

  try {
    const area = document.createElement('textarea')
    area.value = texto
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(area)
    return ok
  } catch (e) {
    return false
  }
}
